import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Mail,
  MessageCircle,
  MessagesSquare,
  MessageSquareText,
  Sparkles,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ChannelHint } from "@/types/chat-external-message";
import {
  ExternalCommunicationChannelKind,
  MessagingChannelConfig,
} from "@/types/messaging-channel-config";
import { labelForHint } from "./utils/channelLabels";

interface ChannelHintPickerProps {
  selected: ChannelHint;
  onSelect: (next: ChannelHint) => void;
  configs: MessagingChannelConfig[];
  unavailableChannels: ReadonlySet<ExternalCommunicationChannelKind>;
  disabled?: boolean;
}

const ICON: Record<ChannelHint, React.ReactNode> = {
  auto: <Sparkles className="size-4 text-skyline-2" />,
  STREAM: <MessagesSquare className="size-4 text-skyline-2" />,
  EMAIL: <Mail className="size-4 text-skyline-2" />,
  SMS: <MessageSquareText className="size-4 text-skyline-2" />,
  WHATSAPP: <MessageCircle className="size-4 text-skyline-2" />,
};

const EXTERNAL_KINDS: readonly ExternalCommunicationChannelKind[] = [
  "EMAIL",
  "SMS",
  "WHATSAPP",
];

export const ChannelHintPicker: FC<ChannelHintPickerProps> = ({
  selected,
  onSelect,
  configs,
  unavailableChannels,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const externalRows = EXTERNAL_KINDS.filter(
    (kind) => !unavailableChannels.has(kind),
  )
    .map((kind) => {
      const config = configs.find((c) => c.channel === kind) ?? null;
      return {
        kind,
        config,
        usable: !!config && config.enabled && config.providerAvailable,
      };
    })
    .filter((r) => r.config !== null);

  const selectedLabel = labelForHint(selected, t);

  const handleSelect = (next: ChannelHint) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={t("communicationChannels.composer.picker.triggerAria")}
          className={cn(
            "h-9 px-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-sm transition-colors",
            "hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-skyline-2/40",
          )}
        >
          {ICON[selected]}
          <span className="font-medium">{selectedLabel}</span>
          <ChevronDown className="size-3.5 text-slate-400" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        className="w-[260px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl"
      >
        <ul
          className="flex flex-col gap-1"
          role="listbox"
          aria-label={t("communicationChannels.composer.picker.listAria")}
        >
          <PickerRow
            value="auto"
            selected={selected}
            onSelect={handleSelect}
            icon={ICON.auto}
            label={t("communicationChannels.composer.picker.auto")}
            sublabel={t("communicationChannels.composer.picker.autoSublabel")}
            accent={
              <Badge variant="verified" className="ml-auto">
                {t("communicationChannels.composer.picker.recommended")}
              </Badge>
            }
          />

          <PickerRow
            value="STREAM"
            selected={selected}
            onSelect={handleSelect}
            icon={ICON.STREAM}
            label={t("communicationChannels.composer.picker.stream")}
            sublabel={t("communicationChannels.composer.picker.streamSublabel")}
          />

          {externalRows.length > 0 && (
            <li
              className="my-1 border-t border-slate-100"
              role="separator"
              aria-hidden
            />
          )}

          {externalRows.map(({ kind, config, usable }) =>
            renderExternalRow({
              kind,
              config: config!,
              usable,
              selected,
              onSelect: handleSelect,
              t,
            }),
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

interface RenderExternalRowArgs {
  kind: ExternalCommunicationChannelKind;
  config: MessagingChannelConfig;
  usable: boolean;
  selected: ChannelHint;
  onSelect: (next: ChannelHint) => void;
  t: (key: string, opts?: Record<string, string>) => string;
}

const renderExternalRow = ({
  kind,
  config,
  usable,
  selected,
  onSelect,
  t,
}: RenderExternalRowArgs): React.ReactNode => {
  const label = t(`communicationChannels.channels.${kind}.label`);
  const sublabel = config.isDefault
    ? t("communicationChannels.composer.picker.isDefault")
    : undefined;
  const accent = config.isDefault ? (
    <Badge variant="new_secondary" className="ml-auto">
      {t("communicationChannels.matrix.defaultBadge")}
    </Badge>
  ) : undefined;

  const row = (
    <PickerRow
      value={kind}
      selected={selected}
      onSelect={onSelect}
      icon={ICON[kind]}
      label={label}
      sublabel={sublabel}
      accent={accent}
      disabled={!usable}
    />
  );
  if (usable) return <React.Fragment key={kind}>{row}</React.Fragment>;

  const tooltipKey = !config.providerAvailable
    ? "communicationChannels.composer.picker.tooltipComingSoon"
    : "communicationChannels.composer.picker.tooltipDisabled";

  return (
    <TooltipProvider key={kind} delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block">{row}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          {t(tooltipKey, { channel: label })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface PickerRowProps {
  value: ChannelHint;
  selected: ChannelHint;
  onSelect: (next: ChannelHint) => void;
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  accent?: React.ReactNode;
  disabled?: boolean;
}

const PickerRow: FC<PickerRowProps> = ({
  value,
  selected,
  onSelect,
  icon,
  label,
  sublabel,
  accent,
  disabled = false,
}) => (
  <li role="option" aria-selected={selected === value}>
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(value)}
      className={cn(
        "flex items-start gap-3 w-full rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
        "hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
        selected === value && "bg-slate-50",
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="flex flex-col min-w-0 flex-1">
        <span className="font-medium text-slate-800 truncate">{label}</span>
        {sublabel && (
          <span className="text-xs text-muted-foreground truncate">
            {sublabel}
          </span>
        )}
      </span>
      {accent}
    </button>
  </li>
);
